/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useState, useRef } from "react";
import DateRangePicker from "src/components/sector-momentum/DateRangePicker";

import {
  fetchWrapper,
  InsiderAcquisition,
  InsiderSummaryData,
} from "src/helpers";

import {
  getDisplayLabel,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import InsiderStateCardSkeleton from "src/components/skeleton/InsiderStateCardSkeleton";
import CategorizationAmtSkeleton from "src/components/skeleton/CategorizationAmtSkeleton";
import InsiderStateCard from "src/components/insiderTrading/InsiderStateCard";
import CategorizationAmt from "src/components/insiderTrading/CategorizationAmt";
import InsiderDetailTable from "src/components/insiderTrading/InsiderDetailTable";
import SubstantialActivity from "src/components/insiderTrading/SubstantialActivity";
import DialogModal from "src/common/DialogModal";

export interface InsiderTrade {
  acquisition_mode: string;
  broadcast_date: string;
  company_name: string;
  holding_change: string;
  person_category: string;
  post_shareholding_percent: string;
  price: string;
  prior_shareholding_percent: string;
  share_count: number;
  stock_symbol: string;
  total_price: number;
  transaction_type: string;
  subInsiderTradingData: Record<string, SubInsiderTrading[]>;
}

export interface SubInsiderTrading {
  acquirer_disposer_name: string;
  acquisition_from_date: string;
  acquisition_to_date: string;
  price: string;
  share_count: number;
  total_price: number;
  transaction_type: string;
}
// Custom hook to get window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

export default function InsiderTradingPage() {
  const [showSubstantialModal, setShowSubstantialModal] = useState(false);
  const today = new Date();
  const fifteenDaysAgo = new Date();
  fifteenDaysAgo.setDate(today.getDate() - 15);
  const [dateRange, setDateRange] = useState({
    start: fifteenDaysAgo,
    end: today,
  });
  const MAX_DATE = today;
  const MIN_DATE = new Date(today);
  MIN_DATE.setFullYear(today.getFullYear() - 1);
  const user = getLocalStorageUser() || {};
  const token = user?.token;
  const [insiderTradingSummary, setInsiderTradingSummary] =
    useState<InsiderSummaryData | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [insiderAcquisition, setInsiderAcquisition] = useState<
    InsiderAcquisition[]
  >([]);
  const [selectedAcquisitionModes, setSelectedAcquisitionModes] =
    useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { width: windowWidth } = useWindowSize();

  // Calculate rows per page based on screen width
  const getRowsPerPage = () => {
    if (windowWidth >= 1999) return 20;
    if (windowWidth >= 1280) return 10;
    if (windowWidth >= 1024) return 10;
    if (windowWidth >= 768) return 10;
    return 10;
  };

  const [rowsPerPage, setRowsPerPage] = useState(getRowsPerPage());
  const [insiderTradingData, setInsiderTradingData] = useState<InsiderTrade[]>(
    [],
  );
  const [transactionType, setTransactionType] = useState([]);
  const [persontype, setPersonType] = useState([]);
  const [marketCapType, setSelectMarketCapType] = useState([]);
  const [symbolType, setSymbolType] = useState([]);
  const [selectedPersonType, setSelectedPersonType] = useState<string[]>([]);
  const [selectedTransactionType, setSelectedTransactionType] = useState<
    string[]
  >([]);
  const [selectMarketCap, setSelectMarketCap] = useState<string[]>([]);

  const [selectSymbol, setSelectSymbol] = useState<string[]>([]);
  const currentRequestRef = useRef<number>(0);
  const [sortConfig, setSortConfig] = useState<{
    key: any;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null,
  });

  const [subDataCache, setSubDataCache] = useState<
    Record<string, SubInsiderTrading[]>
  >({});

  const [subInsiderTradingData, setSubInsiderTradingData] = useState<
    Record<string, SubInsiderTrading[]>
  >({});
  const formatDateforData = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  const fromDate = formatDateforData(dateRange.start);
  const toDate = formatDateforData(dateRange.end);

  const buildParams = (
    options: {
      includePagination?: boolean;
      includeSorting?: boolean;
    } = {},
  ) => {
    const params = new URLSearchParams({
      startDate: fromDate,
      endDate: toDate,
    });

    // Four main filters
    if (selectedPersonType.length > 0)
      params.append("filterBy[categories]", selectedPersonType.join(","));
    if (selectedTransactionType.length > 0)
      params.append(
        "filterBy[transactionTypes]",
        selectedTransactionType.join(","),
      );
    if (selectMarketCap.length > 0)
      params.append("filterBy[marketCaps]", selectMarketCap.join(","));
    if (selectSymbol.length > 0)
      params.append("filterBy[symbols]", selectSymbol.join(","));

    // Optional: pagination
    if (options.includePagination) {
      params.append("page", String(currentPage));
      params.append("limit", String(rowsPerPage));
    }

    // Optional: sorting
    if (options.includeSorting && sortConfig.key && sortConfig.direction) {
      params.append("sortColumn", sortConfig.key);
      params.append("sortOrder", sortConfig.direction);
    }

    if (selectedAcquisitionModes.length > 0) {
      params.append("filterBy[acquisitionModes]", selectedAcquisitionModes);
    }

    return params.toString();
  };

  const handleStartChange = (date: Date) => {
    setDateRange((prev) => ({ ...prev, start: date }));
    setCurrentPage(1);
  };

  const handleEndChange = (date: Date) => {
    setDateRange((prev) => ({ ...prev, end: date }));
    setCurrentPage(1);
  };

  const getInsiderSummaryData = async () => {
    try {
      const queryString = buildParams(); // only the four filters + dates
      const response = await fetchWrapper.get(
        `/api/dashboard/insider/summary?${queryString}`,
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      setInsiderTradingSummary(response.data);
    } catch (error) {
      console.error("Error fetching insider trading summary:", error);
    }
  };

  const getInsiderAquisitionData = async () => {
    try {
      const queryString = buildParams(); // only the four filters + dates
      const response = await fetchWrapper.get(
        `/api/dashboard/insider/acquisition_types?${queryString}`,
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      setInsiderAcquisition(response.data);
    } catch (error) {
      console.error("Error fetching insider trading acquisition types:", error);
    }
  };

  const getInsiderTradingData = async () => {
    const requestId = Date.now() + Math.random();
    currentRequestRef.current = requestId;

    try {
      const queryString = buildParams({
        includePagination: true,
        includeSorting: true,
      });
      const response = await fetchWrapper.get(
        `/api/dashboard/insider?${queryString}`,
        undefined,
        { Authorization: `Bearer ${token}` },
      );

      if (currentRequestRef.current !== requestId) {
        return;
      }

      const convertedPerson = (response.person_categories || []).map(
        (item: string) => ({
          label: getDisplayLabel(item),
          value: item,
        }),
      );
      const convertedTransaction = (response.transaction_types || []).map(
        (item: string) => ({
          label: getDisplayLabel(item),
          value: item,
        }),
      );
      const convertedMarketCap = (response.market_caps || []).map(
        (item: string) => ({
          label: item,
          value: item,
        }),
      );
      const convertedSymbols = (response.symbols || []).map(
        (item: { symbol: string }) => ({
          label: item.symbol,
          value: item.symbol,
        }),
      );

      setInsiderTradingData(response.data);
      setTransactionType(convertedTransaction);
      setPersonType(convertedPerson);
      setSelectMarketCapType(convertedMarketCap);
      setSymbolType(convertedSymbols);
      setTotalItems(response.pagination.totalCount || 10);
      setCurrentPage(response.pagination.page || 1);
    } catch (error) {
      console.error("Error fetching insider trading data:", error);
    }
  };

  const getSubInsiderTradingData = async (
    symbol: string,
    personCategory: string,
    transactionType: string,
    acquisitionMode: string,
    broadcastDate: string,
  ) => {
    const key = `${symbol}-${personCategory}-${transactionType}-${acquisitionMode}-${broadcastDate}`;
    if (subDataCache[key]) {
      setSubInsiderTradingData((prev) => ({
        ...prev,
        [key]: subDataCache[key],
      }));
      return;
    }

    try {
      const response = await fetchWrapper.get(
        `/api/dashboard/insider?symbol=${encodeURIComponent(symbol)}&personCategory=${personCategory}&transactionType=${transactionType}&acquisitionMode=${acquisitionMode}&broadcastDate=${broadcastDate}`,
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      setSubInsiderTradingData((prev) => ({
        ...prev,
        [key]: response.data,
      }));

      setSubDataCache((prev) => ({
        ...prev,
        [key]: response.data,
      }));
    } catch (_error) {
      // alertService.error("Something went wrong. Please try again later.");
    }
  };

  useEffect(() => {
    getInsiderSummaryData();
    getInsiderAquisitionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAcquisitionModes,
    selectedPersonType,
    selectedTransactionType,
    selectMarketCap,
    selectSymbol,
  ]);

  useEffect(() => {
    getInsiderTradingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    selectedPersonType,
    selectedTransactionType,
    selectMarketCap,
    selectSymbol,
    sortConfig,
    selectedAcquisitionModes,
    rowsPerPage,
  ]);

  useEffect(() => {
    const delay = setTimeout(async () => {
      setIsLoading(true);
      await Promise.all([
        getInsiderSummaryData(),
        getInsiderAquisitionData(),
        getInsiderTradingData(),
      ]);
      setIsLoading(false);
    }, 600);

    return () => clearTimeout(delay);
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDaysDifference = (start: Date, end: Date) => {
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.key !== columnKey) {
        return { key: columnKey, direction: "desc" };
      }
      if (prev.direction === "desc")
        return { key: columnKey, direction: "asc" };
      if (prev.direction === "asc") return { key: null, direction: null };
      return { key: columnKey, direction: "desc" };
    });

    setCurrentPage(1);
  };

  return (
    <div className="mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <div className="flex items-start gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-medium text-gray-900 dark:text-white">
              Insider Trading Analytics
            </h1>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {`Analysis in past ${getDaysDifference(dateRange.start, dateRange.end)} days`}
            </p>
          </div>
          <button
            onClick={() => setShowSubstantialModal(true)}
            className="group relative mt-1 flex items-center gap-2 px-2 py-1 rounded-full
              border border-indigo-600/20 dark:border-cyan-400/20
              hover:border-indigo-600/40 dark:hover:border-cyan-400/40
              bg-indigo-50/30 dark:bg-cyan-900/10
              hover:shadow-[0_2px_12px_rgba(99,102,241,0.15)] dark:hover:shadow-[0_2px_12px_rgba(34,211,238,0.15)]
              transition-all duration-300 cursor-pointer"
          >
            {/* Glowing Dot */}
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 bg-indigo-500/20 blur-md rounded-full opacity-50 group-hover:opacity-100 transition-opacity" />
              <span className="animate-ping absolute inline-flex h-2.5 w-2.5 rounded-full bg-indigo-500 dark:bg-cyan-400 opacity-40 group-hover:opacity-60 transition-opacity" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-600 dark:bg-cyan-400" />
            </div>

            {/* Gradient Text with Hover Underline */}
            <span className="bg-gradient-to-r from-indigo-600 to-cyan-500 dark:from-cyan-400 dark:to-indigo-400 bg-clip-text text-transparent text-[14px] font-semibold tracking-wide group-hover:brightness-125 transition-all duration-300 relative dark:after:from-cyan-400 dark:after:to-indigo-400 after:transition-all after:duration-300 group-hover:after:w-full">
              Check Substantial Activity
            </span>

            {/* Chevron Icon corresponding to Cyan/Indigo */}
            <svg
              className="w-3.5 h-3.5 text-cyan-500 dark:text-indigo-400 transform transition-all duration-300 group-hover:translate-x-1 group-hover:brightness-125"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <DialogModal
          title=""
          isinsiderModal={true}
          show={showSubstantialModal}
          setShow={setShowSubstantialModal}
          message={<SubstantialActivity />}
          disableHeight
          maxWidth="max-w-[1500px]"
        />

        <DateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          onStartDateChange={handleStartChange}
          onEndDateChange={handleEndChange}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
        />
      </div>

      <div className="mb-2">
        {isLoading ? (
          <InsiderStateCardSkeleton />
        ) : (
          <InsiderStateCard insiderTradingSummary={insiderTradingSummary} />
        )}
      </div>
      {isLoading ? (
        <CategorizationAmtSkeleton />
      ) : (
        <CategorizationAmt
          insiderAcquisition={insiderAcquisition}
          selectedModes={selectedAcquisitionModes}
          onModeSelect={setSelectedAcquisitionModes}
        />
      )}

      <div className="mt-2 mb-2 bg-white dark:bg-[#1E1E2D] shadow-lg border border-gray-100 dark:border-[#2a2a44]">
        <InsiderDetailTable
          insiderTradingData={insiderTradingData}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalItems={totalItems}
          rowsPerPage={rowsPerPage}
          getSubInsiderTradingData={getSubInsiderTradingData}
          subInsiderTradingData={subInsiderTradingData}
          transactionType={transactionType}
          persontype={persontype}
          marketCapType={marketCapType}
          symbolType={symbolType}
          setSelectedPersonType={setSelectedPersonType}
          setSelectedTransactionType={setSelectedTransactionType}
          selectedPersonType={selectedPersonType}
          selectedTransactionType={selectedTransactionType}
          selectMarketCap={selectMarketCap}
          selectSymbol={selectSymbol}
          setSelectMarketCap={setSelectMarketCap}
          setSelectSymbol={setSelectSymbol}
          handleSort={handleSort}
          sortConfig={sortConfig}
          setRowsPerPage={setRowsPerPage}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
